/** Pieces the Returns page and the lead page share. */
import { Badge, Stack, Text, Tooltip } from '@mantine/core';
import type { LeadReturn } from '../../api/returns';
import { formatDateTime, formatGBP } from '../../theme';

const STATUS: Record<string, { colour: string; label: string }> = {
  requested: { colour: 'blue', label: 'Requested' },
  approved: { colour: 'teal', label: 'Approved' },
  rejected: { colour: 'gray', label: 'Rejected' },
};

export function ReturnStatusBadge({ status }: { status: string }) {
  const s = STATUS[status] ?? { colour: 'gray', label: status };
  return <Badge variant="light" color={s.colour}>{s.label}</Badge>;
}

/** What it was worth: the credit once approved, else what was paid. */
export function returnAmount(r: LeadReturn): string {
  return formatGBP(r.status === 'approved' ? r.credit_amount : r.sale_amount);
}

/** The reason, with the note underneath when there is one. */
export function ReasonCell({ r }: { r: LeadReturn }) {
  return (
    <Stack gap={0}>
      <Text size="sm">{r.reason_label}</Text>
      {r.note && <Text size="xs" c="dimmed" lineClamp={2}>{r.note}</Text>}
      {r.outside_window && (
        <Tooltip label={r.override_reason || 'Outside the return window'} withinPortal>
          <Text size="xs" c="orange">Outside window</Text>
        </Tooltip>
      )}
    </Stack>
  );
}

/** Who asked and who decided, in a line each. */
export function WhoCell({ r }: { r: LeadReturn }) {
  return (
    <Stack gap={0}>
      <Text size="xs">
        {`${r.requested_via === 'buyer' ? 'Asked' : 'Returned'} by ${r.requested_by_name || 'someone'}, ${formatDateTime(r.created_at)}`}
      </Text>
      {r.decided_at && r.requested_via === 'buyer' && (
        <Text size="xs" c="dimmed">
          {`${r.status === 'rejected' ? 'Rejected' : 'Approved'}${r.decided_by_name ? ` by ${r.decided_by_name}` : ''}, ${formatDateTime(r.decided_at)}`}
        </Text>
      )}
      {r.decision_note && <Text size="xs" c="dimmed" lineClamp={2}>{r.decision_note}</Text>}
    </Stack>
  );
}
